import { Vector3 } from 'three';

export type CutSegment = {
  a: Vector3;
  b: Vector3;
};

const KEY_SCALE = 1e4;

function positionKey(p: Vector3): string {
  return `${Math.round(p.x * KEY_SCALE)}:${Math.round(p.y * KEY_SCALE)}:${Math.round(p.z * KEY_SCALE)}`;
}

function edgeKey(a: string, b: string): string {
  return a < b ? `${a}|${b}` : `${b}|${a}`;
}

/**
 * Stitches BVH mesh-intersection segments into one ordered closed loop (largest cycle).
 */
export function orderCutSegmentsIntoLoop(segments: CutSegment[]): Vector3[] {
  if (segments.length === 0) return [];

  const points = new Map<string, Vector3>();
  const adjacency = new Map<string, Set<string>>();

  const link = (a: Vector3, b: Vector3) => {
    const ka = positionKey(a);
    const kb = positionKey(b);
    if (ka === kb) return;
    points.set(ka, a.clone());
    points.set(kb, b.clone());
    if (!adjacency.has(ka)) adjacency.set(ka, new Set());
    if (!adjacency.has(kb)) adjacency.set(kb, new Set());
    adjacency.get(ka)!.add(kb);
    adjacency.get(kb)!.add(ka);
  };

  for (const seg of segments) {
    link(seg.a, seg.b);
  }

  if (adjacency.size === 0) return [];

  const visitedEdges = new Set<string>();
  const cycles: string[][] = [];

  for (const [startKey, neighbors] of adjacency) {
    for (const nextKey of neighbors) {
      const ek = edgeKey(startKey, nextKey);
      if (visitedEdges.has(ek)) continue;

      const cycle: string[] = [startKey];
      let prevKey = startKey;
      let curKey = nextKey;
      visitedEdges.add(ek);

      while (curKey !== startKey && cycle.length <= adjacency.size + 2) {
        cycle.push(curKey);
        const nbrs = adjacency.get(curKey);
        if (!nbrs) break;

        let found = false;
        for (const nKey of nbrs) {
          if (nKey === prevKey) continue;
          const nEk = edgeKey(curKey, nKey);
          if (visitedEdges.has(nEk)) continue;
          visitedEdges.add(nEk);
          prevKey = curKey;
          curKey = nKey;
          found = true;
          break;
        }
        if (!found) break;
      }

      if (cycle.length >= 3) {
        cycles.push(cycle);
      }
    }
  }

  if (cycles.length === 0) {
    return [...points.values()];
  }

  let bestCycle = cycles[0];
  let bestLength = loopLength(bestCycle, points);
  for (let i = 1; i < cycles.length; i++) {
    const len = loopLength(cycles[i], points);
    if (len > bestLength) {
      bestLength = len;
      bestCycle = cycles[i];
    }
  }

  return bestCycle.map((k) => points.get(k)!.clone());
}

function loopLength(keys: string[], points: Map<string, Vector3>): number {
  if (keys.length < 2) return 0;
  let total = 0;
  for (let i = 0; i < keys.length; i++) {
    const a = points.get(keys[i])!;
    const b = points.get(keys[(i + 1) % keys.length])!;
    total += a.distanceTo(b);
  }
  return total;
}

/**
 * Removes consecutive duplicates only (no spacing decimation).
 */
export function dedupeOrderedLoopConsecutive(points: Vector3[], epsilon = 1e-5): Vector3[] {
  if (points.length === 0) return [];
  const out: Vector3[] = [points[0].clone()];
  for (let i = 1; i < points.length; i++) {
    const last = out[out.length - 1];
    if (last.distanceTo(points[i]) > epsilon) {
      out.push(points[i].clone());
    }
  }
  return out.length >= 2 ? out : out.length === 1 ? [out[0].clone(), out[0].clone()] : out;
}

/**
 * Removes consecutive duplicates and keeps points spaced by at least `minSpacing`.
 */
export function decimateOrderedLoop(points: Vector3[], minSpacing: number): Vector3[] {
  if (points.length === 0) return [];
  const deduped: Vector3[] = [];
  for (const p of points) {
    const last = deduped[deduped.length - 1];
    if (!last || last.distanceTo(p) > minSpacing * 0.25) {
      deduped.push(p.clone());
    }
  }
  if (deduped.length <= 2) return deduped;

  const out: Vector3[] = [deduped[0].clone()];
  let lastKept = deduped[0];
  for (let i = 1; i < deduped.length; i++) {
    if (lastKept.distanceTo(deduped[i]) >= minSpacing) {
      out.push(deduped[i].clone());
      lastKept = deduped[i];
    }
  }
  return out.length >= 2 ? out : deduped;
}
